// ══════════════════════════════════════════════════════════════════════
// MarketData Status Widget (Sprint 1.4)
// Zeigt den aktuellen Stand der live MarketData-Streams aus dem Bot.
// Polled /api/marketdata/status alle 5s und rendert pro Symbol einen
// Health-Indikator basierend auf last_bar_age_sec.
// ══════════════════════════════════════════════════════════════════════

import { useApi } from "../hooks/useApi";
import { theme, fonts } from "../theme";
import { Card, SectionTitle } from "./ui";

// Health-Schwellen für last_bar_age_sec
const FRESH_SEC = 60;    // < 1 Min → grün, alles "ok"
const STALE_SEC = 300;   // < 5 Min → amber, noch ok aber alt

function healthColor(age, connected) {
  if (!connected) return { bg: theme.redLight, dot: theme.red, text: theme.redText, label: "DISCONNECTED" };
  if (age === null || age === undefined) return { bg: theme.tint || "#f1f5f9", dot: theme.text4, text: theme.text3, label: "NO DATA" };
  if (age < FRESH_SEC) return { bg: theme.greenLight, dot: theme.green, text: theme.greenText, label: "LIVE" };
  if (age < STALE_SEC) return { bg: theme.amberLight, dot: theme.amber, text: theme.amberText, label: "STALE" };
  return { bg: theme.redLight, dot: theme.red, text: theme.redText, label: "TIMEOUT" };
}

function formatAge(sec) {
  if (sec === null || sec === undefined) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
}

export default function MarketDataWidget() {
  const { data, error, loading } = useApi("/marketdata/status", 5000);

  // Loading state (initial fetch)
  if (loading && !data) {
    return (
      <Card style={{ padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: theme.text3, fontFamily: fonts.body }}>MarketData: lädt…</div>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card style={{ padding: "14px 18px", marginBottom: 16, borderLeft: `3px solid ${theme.red}` }}>
        <div style={{ fontSize: 11, color: theme.redText, fontFamily: fonts.body }}>
          MarketData status error: {String(error)}
        </div>
      </Card>
    );
  }

  // Manager not running (no active adapters)
  if (!data?.manager_running) {
    return (
      <Card style={{ padding: "14px 18px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: theme.text4 || "#94a3b8" }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.text3, textTransform: "uppercase", letterSpacing: "0.8px" }}>
            MarketData
          </span>
          <span style={{ fontSize: 11, color: theme.text3 }}>
            inactive — no paper/live strategies running
          </span>
        </div>
      </Card>
    );
  }

  // Manager running: render per-symbol status
  const streams = data.streams || {};
  const symbols = Object.keys(streams).sort();

  return (
    <div style={{ marginBottom: 20 }}>
      <SectionTitle>MarketData Streams</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(symbols.length, 4) || 1}, 1fr)`, gap: 10 }}>
        {symbols.length === 0 && (
          <Card style={{ padding: "14px 18px" }}>
            <div style={{ fontSize: 11, color: theme.text3 }}>Manager läuft, aber keine Streams registriert.</div>
          </Card>
        )}
        {symbols.map((symbol) => {
          const info = streams[symbol] || {};
          const h = healthColor(info.last_bar_age_sec, info.connected);
          return (
            <Card key={symbol} style={{ padding: "12px 16px", borderLeft: `3px solid ${h.dot}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontFamily: fonts.mono, fontWeight: 800, fontSize: 13, color: theme.text }}>
                  {symbol}
                </span>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "2px 8px", borderRadius: 12,
                  background: h.bg, fontSize: 9, fontWeight: 800, color: h.text,
                  letterSpacing: "0.4px",
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: h.dot }} />
                  {h.label}
                </span>
              </div>
              <div style={{ display: "flex", gap: 14, fontSize: 10, color: theme.text3, fontFamily: fonts.mono }}>
                <span>age: <strong style={{ color: theme.text2 || theme.text }}>{formatAge(info.last_bar_age_sec)}</strong></span>
                <span>consumers: {info.n_consumers ?? 0}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
